import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { 
  Settings, 
  Volume2, 
  VolumeX, 
  Type, 
  Languages, 
  Zap,
  Info
} from "lucide-react";
import { useSettingsStore } from "@/store/settingsStore";
import { getAvailableVoices } from "@/lib/speech";
import { cn } from "@/lib/utils";

interface StudySettingsProps {
  trigger?: React.ReactElement;
}

export default function StudySettings({ trigger }: StudySettingsProps) {
  const settings = useSettingsStore();
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);

  useEffect(() => {
    // Some browsers load voices asynchronously
    const loadVoices = () => {
      const availableVoices = getAvailableVoices();
      if (availableVoices.length > 0) {
        setVoices(availableVoices);
      }
    };

    loadVoices();
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, []);

  const uniqueLanguages = Array.from(new Set(voices.map(v => v.lang))).sort();

  return (
    <Dialog>
      <DialogTrigger
        render={
          trigger || (
            <Button variant="outline" size="icon" className="rounded-xl">
              <Settings className="h-4 w-4" />
            </Button>
          )
        }
      />
      <DialogContent className="sm:max-w-[425px] rounded-[2rem] p-6">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black italic uppercase tracking-tight flex items-center gap-2">
            <Settings className="h-6 w-6 text-primary" />
            Cài đặt học tập
          </DialogTitle>
        </DialogHeader>
        
        <div className="grid gap-6 py-4">
          {/* Auto Read Mode Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                  <Volume2 className="h-4 w-4 text-primary" />
                  Tự động đọc
                </Label>
                <p className="text-xs text-muted-foreground">Chọn mặt thẻ để đọc tự động</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 rounded-2xl bg-zinc-50 border border-zinc-100 transition-all hover:bg-zinc-100/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-primary/10 text-primary">
                    <Type className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-bold leading-tight">Mặt trước</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest">Front side</p>
                  </div>
                </div>
                <Switch 
                  checked={settings.autoReadFront} 
                  onCheckedChange={settings.setAutoReadFront} 
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-2xl bg-zinc-50 border border-zinc-100 transition-all hover:bg-zinc-100/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-primary/10 text-primary">
                    <Type className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-bold leading-tight">Mặt sau</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest">Back side</p>
                  </div>
                </div>
                <Switch 
                  checked={settings.autoReadBack} 
                  onCheckedChange={settings.setAutoReadBack} 
                />
              </div>
            </div>

          </div>

          <div className="h-px bg-muted/50" />

          {/* Language Selection Section */}
          <div className="space-y-4">
            <Label className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
              <Languages className="h-4 w-4 text-primary" />
              Ngôn ngữ đọc
            </Label>
            
            <div className="grid gap-4">
              <div className="space-y-2">
                <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Mặt trước</p>
                <select 
                  className="w-full h-10 px-3 rounded-xl bg-muted/30 border-muted/50 text-sm font-medium outline-none focus:ring-2 focus:ring-primary/20"
                  value={settings.frontLang}
                  onChange={(e) => settings.setFrontLang(e.target.value)}
                >
                  {uniqueLanguages.map(lang => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                  {uniqueLanguages.length === 0 && <option value="en-US">English (US)</option>}
                </select>
              </div>
              
              <div className="space-y-2">
                <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Mặt sau</p>
                <select 
                  className="w-full h-10 px-3 rounded-xl bg-muted/30 border-muted/50 text-sm font-medium outline-none focus:ring-2 focus:ring-primary/20"
                  value={settings.backLang}
                  onChange={(e) => settings.setBackLang(e.target.value)}
                >
                  {uniqueLanguages.map(lang => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                  {uniqueLanguages.length === 0 && <option value="vi-VN">Vietnamese</option>}
                </select>
              </div>
            </div>
          </div>

          {/* Speech Rate Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                <Zap className="h-4 w-4 text-primary" />
                Tốc độ đọc
              </Label>
              <span className="text-xs font-black italic bg-primary/10 text-primary px-2 py-0.5 rounded-lg">{settings.speechRate}x</span>
            </div>
            <input 
              type="range" 
              min="0.5" 
              max="2.0" 
              step="0.1" 
              value={settings.speechRate}
              className="w-full h-1.5 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              onChange={(e) => settings.setSpeechRate(parseFloat(e.target.value))}
            />
          </div>
        </div>

        <DialogFooter className="mt-2">
          <p className="text-[10px] text-muted-foreground italic flex items-center gap-1">
            <Info className="h-3 w-3" />
            Cài đặt được lưu tự động trên trình duyệt này.
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
